import { Badge, Flex, IconButton, Spinner, Text, useColorModeValue, useTheme } from '@chakra-ui/react'
import { ChevronUp } from 'lucide-react'
import React from 'react'
import { CLIENT_CONFIG } from '../../core/client/buildConfig'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { KnowledgeSource } from '../../types/chatTypes'

interface KnowledgeSourceBadgeProps {
  knowledgeSource?: KnowledgeSource
  isSearching?: boolean // Knowledge tool still running — render "Searching …"
  canMinimize?: boolean
  onMinimize?: () => void
}

const KnowledgeSourceBadge: React.FC<KnowledgeSourceBadgeProps> = ({ knowledgeSource, isSearching, canMinimize, onMinimize }) => {
  const theme = useTheme()
  const { t } = useLanguage()

  // Color mode values - improved contrast for dark mode
  const textColor = useColorModeValue('gray.500', 'gray.200')
  const hoverBg = useColorModeValue('gray.100', 'gray.600')
  const isDarkMode = useColorModeValue(false, true)

  let badgeLabel;
  let infoLabel;
  let badgeBgColor;
  let badgeTextColor;

  // Set badge properties based on knowledge source with enhanced contrast for dark mode
  switch (knowledgeSource) {
    case 'INTERNAL_KB':
      badgeLabel = isSearching
        ? t('knowledge_source.badge_internal_searching')
        : t('knowledge_source.badge_internal')
      infoLabel = isSearching
        ? t('knowledge_source.searching_internal')
        : t('knowledge_source.internal_guidelines')
      badgeBgColor = CLIENT_CONFIG.theme.colors.primary
      badgeTextColor = 'white'
      break;

    case 'INTERNET_SEARCH':
      badgeLabel = isSearching
        ? t('knowledge_source.badge_internet_searching')
        : t('knowledge_source.badge_internet')
      infoLabel = isSearching
        ? t('knowledge_source.searching_internet')
        : t('knowledge_source.internet_results')
      badgeBgColor = isDarkMode
        ? `${theme.llmTools.internet.backgroundColor}EE` // Increased opacity for dark mode
        : theme.llmTools.internet.backgroundColor
      badgeTextColor = 'white'
      break;

    default:
      badgeLabel = t('knowledge_source.badge_ai')
      infoLabel = t('knowledge_source.general_info')
      badgeBgColor = CLIENT_CONFIG.theme.colors.primary
      badgeTextColor = 'white'
  }

  const handleMinimizeClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent event bubbling
    onMinimize?.()
  }

  return (
    <Flex align="center" gap={2} justify="space-between" w="100%">
      <Flex align="center" gap={2}>
        <Badge
          bg={badgeBgColor}
          color={badgeTextColor}
          fontSize="sm"
          px={2}
          py={1}
          fontWeight="semibold" // Added for better contrast
        >
          {badgeLabel}
        </Badge>
        {isSearching && <Spinner size="xs" color={textColor} />}
        <Text fontSize="sm" color={textColor} fontWeight="medium">
          {infoLabel}
        </Text>
        {canMinimize && onMinimize && (
        <IconButton
          aria-label={t('common.minimize')}
          icon={<ChevronUp size={16} />}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          onClick={handleMinimizeClick}
          _hover={{ bg: hoverBg }}
        />
      )}
      </Flex>
    </Flex>
  )
}

export default KnowledgeSourceBadge
